import { DICOMApprovalStatus } from '../dicom/dicomConstants';
import { BaseDomainEvent } from './DomainEvent';
import { EventSource } from './EventTypes';

export type StructureSetPropertyUpdates = {
    structureSetLabel?: string;
    structureSetDescription?: string;
    visible?: boolean;
    approvalStatus?: DICOMApprovalStatus;
    reviewerName?: string;
    reviewDate?: string;
    reviewTime?: string;
};

/**
 * Domain event emitted when {@link StructureSet} approval status change is requested
 */
export class StructureSetApprovalChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'STRUCTURE_SET_APPROVAL_CHANGE_REQUESTED';

    constructor(
        structureSetSopUid: string,
        newApprovalStatus: DICOMApprovalStatus,
        reviewerName: string,
        source: EventSource = 'user'
    ) {
        super(
            StructureSetApprovalChangeRequestedEvent.EVENT_TYPE,
            `structure-set-${structureSetSopUid}`,
            { structureSetSopUid, newApprovalStatus, reviewerName, source }
        );
    }

    get structureSetSopUid(): string {
        return this.data.structureSetSopUid;
    }

    get newApprovalStatus(): DICOMApprovalStatus {
        return this.data.newApprovalStatus;
    }

    get reviewerName(): string {
        return this.data.reviewerName;
    }

    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when {@link StructureSet} label change is requested
 */
export class StructureSetLabelChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'STRUCTURE_SET_LABEL_CHANGE_REQUESTED';

    constructor(
        structureSetSopUid: string,
        newLabel: string,
        source: EventSource = 'user'
    ) {
        super(
            StructureSetLabelChangeRequestedEvent.EVENT_TYPE,
            `structure-set-${structureSetSopUid}`,
            { structureSetSopUid, newLabel, source }
        );
    }

    get structureSetSopUid(): string {
        return this.data.structureSetSopUid;
    }

    get newLabel(): string {
        return this.data.newLabel;
    }

    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when {@link StructureSet} description change is requested
 */
export class StructureSetDescriptionChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'STRUCTURE_SET_DESCRIPTION_CHANGE_REQUESTED';

    constructor(
        structureSetSopUid: string,
        newDescription: string,
        source: EventSource = 'user'
    ) {
        super(
            StructureSetDescriptionChangeRequestedEvent.EVENT_TYPE,
            `structure-set-${structureSetSopUid}`,
            { structureSetSopUid, newDescription, source }
        );
    }

    get structureSetSopUid(): string {
        return this.data.structureSetSopUid;
    }

    get newDescription(): string {
        return this.data.newDescription;
    }

    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when {@link StructureSet} visibility change is requested
 */
export class StructureSetVisibilityChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE = 'STRUCTURE_SET_VISIBILITY_CHANGE_REQUESTED';

    constructor(
        structureSetSopUid: string,
        visible: boolean,
        source: EventSource = 'user'
    ) {
        super(
            StructureSetVisibilityChangeRequestedEvent.EVENT_TYPE,
            `structure-set-${structureSetSopUid}`,
            { structureSetSopUid, visible, source }
        );
    }

    get structureSetSopUid(): string {
        return this.data.structureSetSopUid;
    }

    get visible(): boolean {
        return this.data.visible;
    }

    get source(): EventSource {
        return this.data.source;
    }
}

/**
 * Domain event emitted when contour thickness change is requested for all ROIs in a {@link StructureSet}
 */
export class StructureSetContourThicknessChangeRequestedEvent extends BaseDomainEvent {
    static readonly EVENT_TYPE =
        'STRUCTURE_SET_CONTOUR_THICKNESS_CHANGE_REQUESTED';

    constructor(
        structureSetSopUid: string,
        contourThickness: number,
        source: EventSource = 'user'
    ) {
        super(
            StructureSetContourThicknessChangeRequestedEvent.EVENT_TYPE,
            `structure-set-${structureSetSopUid}`,
            { structureSetSopUid, contourThickness, source }
        );
    }

    get structureSetSopUid(): string {
        return this.data.structureSetSopUid;
    }

    get contourThickness(): number {
        return this.data.contourThickness;
    }

    get source(): EventSource {
        return this.data.source;
    }
}
